import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-hot-toast";
import { STATUSES } from "../globals/misc/statuses";
import { API, APIAuthenticated } from "../globals/http";

const potholeSlice = createSlice({
  name: "pothole",
  initialState: {
    potholes: [],
    pagination: null,
    singlePothole: null,
    allComments: [],
    status: STATUSES.IDLE,
    votingStatus: STATUSES.IDLE,
  },
  reducers: {
    setPotholes(state, action) {
      state.potholes = action.payload;
    },
    setPagination(state, action) {
      state.pagination = action.payload;
    },
    setStatus(state, action) {
      state.status = action.payload;
    },
    setSinglePothole(state, action) {
      state.singlePothole = action.payload;
    },
    setAllComments(state, action) {
      state.allComments = action.payload;
    },
    setVotingStatus(state, action) {
      state.votingStatus = action.payload;
    },
    updatePotholeVotes(state, action) {
      const { potholeId, upvotes, downvotes } = action.payload;

      state.potholes = state.potholes.map((pothole) =>
        pothole._id === potholeId
          ? {
              ...pothole,
              votes: { ...pothole.votes, upvotes, downvotes },
            }
          : pothole
      );

      if (state.singlePothole && state.singlePothole._id === potholeId) {
        state.singlePothole = {
          ...state.singlePothole,
          votes: {
            ...state.singlePothole.votes,
            upvotes,
            downvotes,
          },
        };
      }
    },
    prependComment(state, action) {
      state.allComments = [...state.allComments, action.payload];
    },
  },
});

export const {
  setPotholes,
  setPagination,
  setStatus,
  setSinglePothole,
  setAllComments,
  setVotingStatus,
  updatePotholeVotes,
  prependComment,
} = potholeSlice.actions;
export default potholeSlice.reducer;

export function fetchPotholes(params = {}) {
  return async function fetchPotholesThunk(dispatch) {
    dispatch(setStatus(STATUSES.LOADING));
    try {
      const response = await API.get("/potholes", { params });
      if (response.data?.success) {
        const payload = response.data.data;
        const items = Array.isArray(payload) ? payload : payload?.items || [];
        dispatch(setPotholes(items));
        dispatch(setPagination(payload?.pagination || null));
        dispatch(setStatus(STATUSES.SUCCESS));
      } else {
        throw new Error(response.data?.message || "Failed to fetch potholes");
      }
    } catch (error) {
      console.error(error.message);
      dispatch(setStatus(STATUSES.ERROR));
    }
  };
}

export function fetchSinglePothole(id) {
  return async function fetchSinglePotholeThunk(dispatch) {
    dispatch(setStatus(STATUSES.LOADING));
    try {
      const response = await API.get(`/potholes/${id}`);
      if (response.data?.success) {
        dispatch(setSinglePothole(response.data.data));
        dispatch(setStatus(STATUSES.SUCCESS));
      } else {
        throw new Error(response.data?.message || "Failed to fetch pothole");
      }
    } catch (error) {
      console.error(error.message);
      dispatch(setStatus(STATUSES.ERROR));
    }
  };
}

export function fetchAllComment(id) {
  return async function fetchAllCommentThunk(dispatch) {
    try {
      const response = await API.get(`/potholes/${id}/comments`);
      if (response.data?.success) {
        dispatch(setAllComments(response.data.data || []));
      }
    } catch (error) {
      console.error(error.message);
    }
  };
}

export function voteOnPothole({ potholeId, voteType }) {
  return async function voteOnPotholeThunk(dispatch) {
    dispatch(setVotingStatus(STATUSES.LOADING));

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = await APIAuthenticated.put(`/potholes/${potholeId}/vote`, {
        voteType,
      });

      if (response.data?.success) {
        const { upvotes, downvotes } = response.data.data;
        dispatch(updatePotholeVotes({ potholeId, upvotes, downvotes }));
        dispatch(setVotingStatus(STATUSES.SUCCESS));
        return response.data;
      }

      throw new Error(response.data?.message || "Failed to process vote");
    } catch (error) {
      console.error("Voting error:", error.message);
      dispatch(setVotingStatus(STATUSES.ERROR));
      toast.error(error.response?.data?.message || error.message);
      throw error;
    }
  };
}

export function addPotholeComment({ potholeId, text }) {
  return async function addPotholeCommentThunk(dispatch) {
    const response = await APIAuthenticated.post(
      `/potholes/${potholeId}/comments`,
      { text }
    );

    if (response.data?.success) {
      dispatch(prependComment(response.data.data));
      return response.data.data;
    }

    throw new Error(response.data?.message || "Failed to post comment");
  };
}

export function createPotholeReport(formData) {
  return async function createPotholeReportThunk() {
    const response = await APIAuthenticated.post("/potholes", formData);

    if (response.data?.success) {
      return response.data.data;
    }

    throw new Error(response.data?.message || "Failed to report pothole");
  };
}
